import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, ArrowRight, Zap, Building2, Sparkles } from 'lucide-react';

function PricingPage() {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');

  const pricingTiers = [
    {
      name: 'Persona',
      tagline: 'Personal Integrity Checks',
      icon: Zap,
      pricePerCheck: 500,
      description: 'Essential verification for household staff, domestic hiring, and personal character assessment',
      checksIncluded: [
        { name: 'National ID (NIN) Verification', included: true },
        { name: 'Phone Number Verification', included: true },
        { name: 'Basic Address Confirmation', included: true },
        { name: 'Reference Check (1 referee)', included: true },
        { name: 'Criminal Records Search', included: true },
        { name: 'BVN Verification', included: false },
        { name: 'Employment History', included: false },
        { name: 'Biometric Liveness', included: false },
        { name: 'Field Agent Visit', included: false },
      ],
      idealFor: ['Domestic Hiring', 'Household Staff', 'Personal Assistants', 'Caregivers'],
      turnaround: '24-48 hours',
      popular: false,
      color: 'blue'
    },
    {
      name: 'Vault',
      tagline: 'Core Business Onboarding',
      icon: Building2,
      pricePerCheck: 1200,
      description: 'Comprehensive verification bundle for SMB customer and staff onboarding with strong trust foundation',
      checksIncluded: [
        { name: 'National ID (NIN) Verification', included: true },
        { name: 'BVN Verification', included: true },
        { name: 'Phone Number Verification', included: true },
        { name: 'Email Verification', included: true },
        { name: 'Biometric Liveness Check', included: true },
        { name: 'Address Verification (Digital)', included: true },
        { name: 'Reference Check (2 referees)', included: true },
        { name: 'Criminal Records Search', included: true },
        { name: 'Basic Employment History', included: true },
        { name: 'Field Agent Visit', included: false },
        { name: 'Asset Verification', included: false },
      ],
      idealFor: ['SMBs', 'Fintech Onboarding', 'E-commerce KYC', 'Staff Recruitment'],
      turnaround: '24-48 hours',
      popular: true,
      color: 'green'
    },
    {
      name: 'Matrix',
      tagline: 'Enterprise Integration Hub',
      icon: Sparkles,
      pricePerCheck: 2500,
      description: 'Multi-system verification orchestration for large-scale compliance and cross-border operations',
      checksIncluded: [
        { name: 'All Vault checks included', included: true },
        { name: 'Multi-Country ID Verification', included: true },
        { name: 'Advanced Biometric (Face + Fingerprint)', included: true },
        { name: 'Field Agent Address Visit', included: true },
        { name: 'Employment History (Full 5 years)', included: true },
        { name: 'Education Credential Verification', included: true },
        { name: 'Professional License Verification', included: true },
        { name: 'Fraud Risk Scoring (SCS 0-1000)', included: true },
        { name: 'Device Fingerprinting', included: true },
        { name: 'Real-time Watchlist Screening', included: true },
        { name: 'Asset Verification (Vehicle/Property)', included: true },
        { name: 'API Orchestration & Custom Workflows', included: true },
      ],
      idealFor: ['Large Enterprises', 'Banks & Financial Institutions', 'Government Agencies', 'Multi-National Corps'],
      turnaround: '48-72 hours',
      popular: false,
      color: 'purple'
    },
    {
      name: 'Sentinel',
      tagline: 'National Security-Grade',
      icon: Shield,
      pricePerCheck: 'Custom',
      priceLabel: 'On Request',
      description: 'Bespoke ultra-deep verification for high-risk positions, national security, and executive-level screening',
      checksIncluded: [
        { name: 'All Matrix checks included', included: true },
        { name: 'International Criminal Records', included: true },
        { name: 'Interpol & Global Watchlist Check', included: true },
        { name: 'Social Media & Digital Footprint Analysis', included: true },
        { name: 'Financial Background Investigation', included: true },
        { name: 'Court Records & Legal History', included: true },
        { name: 'Media & Reputation Screening', included: true },
        { name: 'Field Investigation (Multiple Visits)', included: true },
        { name: 'Polygraph Coordination (Optional)', included: true },
        { name: 'Executive Risk Assessment', included: true },
        { name: 'Continuous Monitoring (Optional)', included: true },
        { name: 'White-Glove Case Management', included: true },
        { name: 'Detailed PDF Report + Briefing', included: true },
      ],
      idealFor: ['National Security', 'Executive Hiring', 'High Net Worth Protection', 'Critical Infrastructure'],
      turnaround: '5-10 business days',
      popular: false,
      color: 'red'
    }
  ];

  const modularPricing = [
    { service: 'Basic ID Check (NIN/BVN)', price: '₦150', volume: 'per verification' },
    { service: 'Document Verification (Passport/DL)', price: '₦250', volume: 'per verification' },
    { service: 'Biometric Liveness Check', price: '₦200', volume: 'per check' },
    { service: 'Address Verification (Digital)', price: '₦300', volume: 'per address' },
    { service: 'Field Agent Visit', price: '₦2,500', volume: 'per visit' },
    { service: 'Employment History Check', price: '₦1,000', volume: 'per check' },
    { service: 'Criminal Records Check', price: '₦500', volume: 'per check' },
    { service: 'Asset Verification', price: '₦3,000', volume: 'per asset' },
  ];

  const volumeDiscounts = [
    { range: '1 - 500', discount: '0%', note: 'Standard pricing' },
    { range: '501 - 2,000', discount: '10%', note: 'Small business discount' },
    { range: '2,001 - 10,000', discount: '20%', note: 'Growth business discount' },
    { range: '10,001 - 50,000', discount: '30%', note: 'Enterprise discount' },
    { range: '50,000+', discount: '40%', note: 'Contact for custom pricing' },
  ];

  const getColorClasses = (color, type = 'bg') => {
    const colors = {
      blue: {
        bg: 'bg-blue-100',
        text: 'text-blue-600',
        border: 'border-blue-500',
        button: 'bg-blue-600 hover:bg-blue-700'
      },
      green: {
        bg: 'bg-green-100',
        text: 'text-green-600',
        border: 'border-green-500',
        button: 'bg-green-600 hover:bg-green-700'
      },
      purple: {
        bg: 'bg-purple-100',
        text: 'text-purple-600',
        border: 'border-purple-500',
        button: 'bg-purple-600 hover:bg-purple-700'
      },
      red: {
        bg: 'bg-red-100',
        text: 'text-red-600',
        border: 'border-red-500',
        button: 'bg-red-600 hover:bg-red-700'
      }
    };
    return colors[color][type];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <img 
              src="/hakicheck-logo.png" 
              alt="HakiCheck" 
              className="h-12 cursor-pointer"
              onClick={() => navigate('/')}
            />
            <button
              onClick={() => navigate('/auth')}
              className="px-6 py-2 bg-[#5a738f] hover:bg-[#4a5f7f] text-white font-medium rounded-lg transition-colors"
            >
              Sign In
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Pay-As-You-Use Verification Packages
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            No subscriptions. No commitments. Pay only for the verifications you need. Choose packages based on depth and complexity.
          </p>

          <div className="inline-flex items-center bg-blue-50 border border-blue-200 rounded-lg px-6 py-3 mb-8">
            <p className="text-blue-800 font-medium">
              💡 All packages are priced per verification • Volume discounts apply automatically
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingTiers.map((tier) => {
              const Icon = tier.icon;
              return (
                <div
                  key={tier.name}
                  className={`bg-white rounded-2xl shadow-xl overflow-hidden transition-transform hover:scale-105 ${
                    tier.popular ? 'ring-4 ring-[#5a738f]' : ''
                  }`}
                >
                  {tier.popular && (
                    <div className="bg-[#5a738f] text-white text-center py-2 font-semibold text-sm">
                      MOST POPULAR
                    </div>
                  )}
                  
                  <div className="p-8">
                    <div className={`inline-flex p-3 rounded-lg ${getColorClasses(tier.color, 'bg')} mb-4`}>
                      <Icon className={getColorClasses(tier.color, 'text')} size={32} />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">{tier.name}</h3>
                    <p className="text-gray-600 text-sm mb-4">{tier.tagline}</p>
                    
                    <div className="mb-6">
                      <div className="text-4xl font-bold text-gray-900">
                        {typeof tier.pricePerCheck === 'number' 
                          ? `₦${tier.pricePerCheck.toLocaleString()}`
                          : tier.priceLabel
                        }
                      </div>
                      <div className="text-gray-600 text-sm">
                        per verification
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        Turnaround: {tier.turnaround}
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm mb-6">{tier.description}</p>

                    <button
                      onClick={() => navigate('/auth')}
                      className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors mb-6 ${getColorClasses(tier.color, 'button')}`}
                    >
                      Get Started
                    </button>

                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Checks Included:</h4>
                    </div>

                    <div className="space-y-3">
                      {tier.checksIncluded.map((check, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          {check.included ? (
                            <Check size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                          ) : (
                            <X size={20} className="text-gray-300 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={`text-sm ${check.included ? 'text-gray-700' : 'text-gray-400'}`}>
                            {check.name}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h4 className="font-semibold text-gray-900 mb-2 text-sm">Ideal For:</h4>
                      <div className="flex flex-wrap gap-2">
                        {tier.idealFor.map((useCase, idx) => (
                          <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                            {useCase}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Modular Pricing */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Pay-As-You-Go Modular Pricing</h2>
            <p className="text-xl text-gray-600">Build your own verification package. Only pay for what you need.</p>
          </div>

          <div className="bg-slate-50 rounded-2xl overflow-hidden shadow-lg">
            <table className="w-full">
              <thead className="bg-[#5a738f] text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Service</th>
                  <th className="px-6 py-4 text-right font-semibold">Price</th>
                  <th className="px-6 py-4 text-right font-semibold">Unit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {modularPricing.map((item, idx) => (
                  <tr key={idx} className="hover:bg-white transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">{item.service}</td>
                    <td className="px-6 py-4 text-right text-[#5a738f] font-bold">{item.price}</td>
                    <td className="px-6 py-4 text-right text-gray-600 text-sm">{item.volume}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              💡 <strong>Pro Tip:</strong> Combine multiple services in your verification workflow. 
              Standard tier includes 500 verifications/month at discounted rates!
            </p>
          </div>
        </div>
      </section>

      {/* Volume Discounts */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Volume Discounts</h2>
            <p className="text-xl text-gray-600">The more you verify, the more you save</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {volumeDiscounts.map((tier, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-xl transition-shadow">
                <div className="text-sm font-medium text-gray-600 mb-2">{tier.range}</div>
                <div className="text-3xl font-bold text-[#5a738f] mb-2">{tier.discount}</div>
                <div className="text-xs text-gray-500">{tier.note}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/auth')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#5a738f] hover:bg-[#4a5f7f] text-white font-semibold rounded-lg transition-colors"
            >
              Start Saving Today
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How does pay-as-you-use work?</h3>
              <p className="text-gray-600">You're charged per verification completed. No monthly fees, no subscriptions. Simply select your package (Persona, Vault, Matrix, or Sentinel) and pay only for the checks you run.</p>
            </div>
            
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What's included in each package?</h3>
              <p className="text-gray-600">Each package is a bundle of verification checks. Persona includes 5 basic checks, Vault includes 9 comprehensive checks, Matrix includes 12+ enterprise checks, and Sentinel includes 13+ security-grade checks with bespoke investigation.</p>
            </div>
            
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Do volume discounts apply?</h3>
              <p className="text-gray-600">Yes! Volume discounts are applied automatically: 10% off for 501-2,000 checks, 20% off for 2,001-10,000, 30% off for 10,001-50,000, and up to 40% off for 50,000+ checks per month.</p>
            </div>
            
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I mix different packages?</h3>
              <p className="text-gray-600">Absolutely! Use Persona for domestic staff, Vault for customer onboarding, and Matrix for high-value clients — all in the same account. You're billed per verification based on the package used.</p>
            </div>
            
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What if I need custom checks?</h3>
              <p className="text-gray-600">For unique requirements beyond our packages, contact our team for Sentinel bespoke verification or create custom verification workflows combining individual check parameters.</p>
            </div>
            
            <div className="pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Is there a minimum commitment?</h3>
              <p className="text-gray-600">No minimum commitment required. Start with a single verification and scale as needed. Pay-as-you-use means complete flexibility.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#5a738f] to-[#4a5f7f] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Start Verifying?</h2>
          <p className="text-xl mb-8 opacity-90">No subscriptions. No setup fees. Pay only for verifications you complete.</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={() => navigate('/auth')}
              className="px-8 py-4 bg-white text-[#5a738f] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Get Started Now
            </button>
            <button
              onClick={() => navigate('/products')}
              className="px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-[#5a738f] transition-colors"
            >
              View Products
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">© 2025 HakiCheck. Trust Infrastructure for Africa.</p>
        </div>
      </footer>
    </div>
  );
}

export default PricingPage;
